type TablePreviewResult = {
  tableCount: number;
  tableSummary: string;
  plainText: string;
  preview: string;
};

function decodeBasicHtmlEntities(text: string): string {
  return text
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, h) => String.fromCharCode(parseInt(h, 16)))
    .replace(/&nbsp;/gi, " ")
    .replace(/&quot;/g, '"')
    .replace(/&#0*39;/g, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&");
}

function stripTablesPreservingOtherHtml(html: string): { withoutTablesHtml: string; tableCount: number } {
  const s = String(html ?? "");
  const tableCount = (s.match(/<\s*table\b/gi) || []).length;
  if (!tableCount) return { withoutTablesHtml: s, tableCount: 0 };
  // Remove each <table ...>...</table> block (handles Excel/Word HTML payloads).
  const withoutTablesHtml = s.replace(/<\s*table\b[\s\S]*?<\/\s*table\s*>/gi, " ");
  return { withoutTablesHtml, tableCount };
}

function htmlToPlainText(html: string): string {
  if (!html) return "";
  let s = String(html);
  // Normalize common block separators to spaces before stripping tags
  s = s.replace(/<\s*br\s*\/?>/gi, " ");
  s = s.replace(/<\/\s*(p|div|li|tr|td|th|h[1-6])\s*>/gi, " ");
  s = s.replace(/<[^>]+>/g, "");
  s = s.replace(/\s+/g, " ").trim();
  return decodeBasicHtmlEntities(s);
}

function tableSummary(count: number): string {
  if (count <= 0) return "";
  if (count === 1) return "Shared a table";
  return `Shared ${count} tables`;
}

/**
 * Notification/latest-message preview logic:
 * - Count <table> tags.
 * - Remove full <table>...</table> blocks before generating plain text.
 * - If both text + table: `${text} • ${Shared N tables}`
 * - If only tables: `Shared a table` / `Shared N tables`
 */
export function formatPreviewWithTables(message: string | null | undefined): TablePreviewResult {
  const raw = String(message ?? "");
  const { withoutTablesHtml, tableCount } = stripTablesPreservingOtherHtml(raw);
  // Office/Excel sometimes leaves stray quote chars around fragments; trim them from the tail
  // so we never render: `text ' • Shared N tables`.
  const plainText = htmlToPlainText(withoutTablesHtml).replace(/[\s'"“”‘’]+$/g, "").trim();
  const summary = tableSummary(tableCount);

  let preview = plainText;
  if (tableCount > 0) {
    preview = plainText ? `${plainText} • ${summary}` : summary;
  }

  return {
    tableCount,
    tableSummary: summary,
    plainText,
    preview: preview.trim(),
  };
}

